import {
  type MutationResult,
  useMutation,
  useQuery,
} from '@apollo/client/react';
import type {
  PrivacyPolicyQuestion,
  Qualification,
  Role,
} from '@seek/ca-graphql-schema/types';
import { sortRoles } from '@seek/ca-myprofile-utils';
import { useMelwaysInfo } from '@seek/melways-react';
import { metrics } from '@seek/metrics-js';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Button,
  Divider,
  IconSend,
  Inline,
  Stack,
} from 'braid-design-system';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { type Control, useFormContext } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import { gql } from 'src/__generated__';
import type {
  ApplySubmitApplicationMutation,
  CoverLetterInput,
  Resume,
  SelectionCriteriaInput,
  SubmitApplicationFailure,
  SubmitApplicationInput,
} from 'src/__generated__/graphql';
import { useQueryLicences } from 'src/components/Licences/hooks/useQueryLicences.tsx';
import PersonalDetails from 'src/components/PersonalDetails';
import {
  LOCAL_STORAGE_KEY,
  REFERENCES_SELECTION_NAMESPACE,
  REFERENCES_ID_NAMESPACE,
  removeSessionStorage,
} from 'src/components/References/ReferencesList';
import { ROLE_REQUIREMENT_NAMESPACE } from 'src/components/RoleRequirementsForm/RoleRequirementsForm';
import {
  isFreeTextQuestion,
  isSingleChoiceQuestion,
  questionIdToFieldKey,
} from 'src/components/RoleRequirementsForm/utils';
import { COVER_LETTER_KEY, SELECTION_CRITERIA_KEY } from 'src/constants/app';
import { useConfig } from 'src/context/config';
import {
  ActionOrigin,
  Page,
  PageSection,
  PageSubSection,
} from 'src/context/tracking';
import { useApplicationCorrelationId } from 'src/context/use-application-correlation-id';
import { useLayoutEffectOnce } from 'src/context/use-layout-effect-once';
import { usePromises } from 'src/context/use-promises.tsx';
import { useJobId } from 'src/context/useJobId';
import { FileAttachment } from 'src/forms/common/AttachmentSelect/AttachmentSelect';
import { useAllQuestions } from 'src/hooks/useAllQuestions/useAllQuestions';
import { useGetVerifications } from 'src/hooks/useGetVerifications';
import { useJobDetails } from 'src/hooks/useJobDetails';
import { usePriorityApplyCredit } from 'src/hooks/usePriorityApplyCredit';
import { logError, logInfo } from 'src/modules/error-logger/browserLog';
import { SET_DEFAULT_RESUME } from 'src/modules/graphql-client/mutations/setDefaultResume.graphql';
import { SUBMIT_APPLICATION } from 'src/modules/graphql-client/mutations/submitApplication.graphql';
import {
  getOperationNameFromQuery,
  setLastApplicationLocalStorage,
} from 'src/modules/utils';
import type { DocumentsFormValues } from 'src/pages/Documents/Documents';
import { useDocuments } from 'src/pages/Documents/hooks/useDocuments';
import { useDocumentsMutations } from 'src/pages/Documents/hooks/useDocumentsMutations';
import { useUnconfirmedData } from 'src/pages/Profile/hooks/useUnconfirmedData.tsx';
import { AlertMessage } from 'src/ui/AlertMessage/AlertMessage';
import PageLoader from 'src/ui/PageLoader/PageLoader';
import { ScrollOnMount } from 'src/ui/ScrollOnMount/ScrollOnMount.tsx';
import { addLogByKey, getLog } from 'src/utils/logger';

import translations from './ReviewPage.vocab';
import { DigitalIdentityToggle } from './components/DigitalIdentityToggle/DigitalIdentityToggle';
import { CoverLetterItem } from './components/DocumentsSummary/CoverLetterItem';
import DocumentsSummary from './components/DocumentsSummary/DocumentsSummary';
import { ResumeItem } from './components/DocumentsSummary/ResumeItem';
import { SelectionCriteriaItem } from './components/DocumentsSummary/SelectionCriteriaItem';
import { PrivacyPolicy } from './components/PrivacyPolicy/PrivacyPolicy';
import { PrivacyPolicyInput } from './components/PrivacyPolicyInput/PrivacyPolicyInput';
import ProfileSummary from './components/ProfileSummary/ProfileSummary';
import { ProfileVisibilitySection } from './components/ProfileVisibility/ProfileVisibilitySection';
import RoleRequirementsSummary from './components/RoleRequirementsSummary/RoleRequirementsSummary';
import { useProfileVisibilityOptions } from './hooks/useProfileVisibilityOptions/useProfileVisibilityOptions';
import { useTracking } from './hooks/useTracking/useTracking';

export const REVIEW_PAGE = gql(`
  query ReviewPage(
    $zone: Zone!
    $languageCode: LanguageCodeIso!
    $locale: Locale!
  ) {
    viewer {
      _id
      profileVisibility {
        level
      }
      profileVisibility2 {
        id
      }
      roles {
        ...role
      }
      skills {
        ...skill
      }
      qualifications(
        includeVerified: true
        status: confirmed
        zone: $zone
        languageCode: $languageCode
      ) {
        ...qualification
      }
      referenceChecks {
        id
        references {
          refereeFullName
          organisationName
          relationshipText(locale: $locale)
          fromDate {
            shortAbsoluteLabel(locale: $locale)
          }
          toDate {
            shortAbsoluteLabel(locale: $locale)
          }
          candidateRoleTitle
          refereeRoleTitle
          referenceDate {
            shortAbsoluteLabel(locale: $locale)
          }
        }
      }         
    }
  }
`);

interface PrivacyPolicyInput {
  id: string;
  value: boolean;
}

interface Props {
  formPayload: Partial<SubmitApplicationInput>;
  isNewToWorkforce: boolean;
  onNewToWorkforceToggle: (toggle: boolean) => void;
}

interface ReviewFormValues extends DocumentsFormValues {
  profilePrivacyLevel: string;
  privacyPolicy?: PrivacyPolicyInput;
  [ROLE_REQUIREMENT_NAMESPACE]: Record<string, string | string[]>;
  [REFERENCES_SELECTION_NAMESPACE]: string;
  [REFERENCES_ID_NAMESPACE]: string;
  isShowStrongInterest?: boolean;
}

const ReviewPage = ({
  formPayload,
  isNewToWorkforce,
  onNewToWorkforceToggle,
}: Props) => {
  const { jobId } = useJobId();
  const { config } = useConfig();
  const { language } = useMelwaysInfo();
  const { t } = useTranslations(translations);
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationCorrelationId } = useApplicationCorrelationId();
  const { getPromiseForKey } = usePromises();
  const { options: profileVisibilityOptions } = useProfileVisibilityOptions();
  const [isShowStrongInterest, setIsShowStrongInterest] = useState(false);

  const { hasDigitalIdentityNudge } = useGetVerifications();
  const {
    priorityApplyCredit,
    priorityApplyEnabled,
    priorityApplyCreditEnabled,
  } = usePriorityApplyCredit();
  const nextRenewalDate =
    priorityApplyCredit?.credit2?.nextRenewalDate?.shortAbsoluteLabel || '';
  const remainingCount = Math.min(
    priorityApplyCredit?.credit2?.totalCredit ?? 3,
    priorityApplyCredit?.credit2?.remainingCount || 0,
  );
  const isUnverified =
    hasDigitalIdentityNudge && priorityApplyEnabled && remainingCount > 0;
  const isVerified =
    !hasDigitalIdentityNudge &&
    priorityApplyCreditEnabled &&
    remainingCount > 0;

  const {
    data: { licences },
  } = useQueryLicences();

  const [submitApplication, submitState] = useMutation(SUBMIT_APPLICATION);
  const [setDefaultResume] = useMutation(SET_DEFAULT_RESUME);

  const { resumes, shouldRenderselectionCriteria } = useDocuments();

  const {
    handleSubmit,
    control,
    watch,
    reset: resetRoot,
    getValues: getRootValues,
    setValue,
    getValues,
    clearErrors,
    formState,
  } = useFormContext<ReviewFormValues>();
  const referencesIdNamespace = getValues(REFERENCES_ID_NAMESPACE);
  const referencesSelectionNamespace = getValues(
    REFERENCES_SELECTION_NAMESPACE,
  );

  const { data, loading, error } = useQuery(REVIEW_PAGE, {
    variables: {
      zone: config.ZONE,
      languageCode: language,
      locale: config.VOCAB_LANGUAGE,
    },
  });

  useEffect(() => {
    if (!error) {
      return;
    }

    logError(
      {
        type: 'review-page-graphql',
        message: 'Failed to fetch profile privacy level',
      },
      {
        error,
        operationName: getOperationNameFromQuery(REVIEW_PAGE),
      },
    );
  }, [error]);

  const profilePrivacyId = data?.viewer?.profileVisibility2?.id;

  const { generateCoverletterUri, generateSelectionCriteriaUri } =
    useDocumentsMutations();
  const rootCoverLetterMethod = watch('coverLetter-method');
  const rootCoverLetterFile = watch('coverLetter-file');
  const rootCoverLetterText = watch('coverLetter-text');
  const rootSelectionCriteriaMethod = watch('selectionCriteria-method');
  const rootSelectionCriteriaFile = watch('selectionCriteria-file');
  const rootSelectionCriteriaText = watch('selectionCriteria-text');

  const selectedResumeId = watch('resume-select');
  const resume =
    watch('resume-method') !== FileAttachment.NONE
      ? resumes.find((item) => item.id === selectedResumeId)
      : null;

  const { trackSubmitClick, trackSubmitSucceeded } = useTracking({
    currentPage: Page.REVIEW_PAGE,
    pageSection: PageSection.REVIEW_APPLICATION,
    isCandidateNewToWorkforce: isNewToWorkforce,
  });

  const { data: jobDetails } = useJobDetails();
  const [pageViewedFired, setPageViewedFired] = useState(false);
  const advertiser = jobDetails?.advertiser?.name || '';

  const unconfirmedData = useUnconfirmedData({
    skip: Boolean(formPayload.resume),
  });

  useLayoutEffectOnce(() => {
    setPageViewedFired(true);
  });

  const { ZONE: zone } = config;

  const { data: questionsWithOptionIds } = useAllQuestions();
  const showQuestionsSummary = Boolean(questionsWithOptionIds.length);

  const submitForm = handleSubmit(async (formValues) => {
    const hasValidResumeId =
      resume != null && typeof resume.id === 'string' && resume.id.length > 0;
    // Set default resume if none is set
    const hasDefaultResume = resumes.some((r) => r.isDefault);
    const shouldSetDefaultResume =
      hasDefaultResume === false && hasValidResumeId;

    if (shouldSetDefaultResume) {
      try {
        await setDefaultResume({
          variables: {
            resumeId: { id: resume.id },
            languageCode: language,
          },
        });
      } catch (setDefaultResumeError) {
        logError(
          {
            type: 'set-default-resume-error',
            message: 'Error setting default resume',
          },
          {
            error: setDefaultResumeError,
            resumeId: resume?.id,
            operationName: getOperationNameFromQuery(SET_DEFAULT_RESUME),
          },
        );
      }
    }

    // Submit application
    const processStart = new Date();
    setValue('isShowStrongInterest', isShowStrongInterest);

    // @ts-ignore Role type slightly different to types from myprofile-utils
    const latestRole = sortRoles<Role>(data?.viewer.roles || [])[0];
    const mostRecentRole = latestRole
      ? {
          company: latestRole.company.text,
          title: latestRole.title.text,
          started: latestRole.from,
          finished: latestRole.to || undefined,
        }
      : undefined;

    const questionnaire: Record<string, string | string[]> = getRootValues(
      ROLE_REQUIREMENT_NAMESPACE,
    );

    const questionnaireAnswers = Object.entries(questionnaire).map(
      ([id, value]) => {
        const question = questionsWithOptionIds.find(
          (q) => questionIdToFieldKey(q.id) === id,
        );

        if (isFreeTextQuestion(question)) {
          return {
            questionId: question?.id || '',
            answers: [{ text: value as string }],
          };
        }

        const options = question?.options || [];

        if (isSingleChoiceQuestion(question)) {
          return {
            questionId: question?.id || '',
            answers: options.filter((o) => value === o.id),
          };
        }

        return {
          questionId: question?.id || '',
          answers: options.filter((o) => value.includes(o.id)),
        };
      },
    );

    if (formValues.privacyPolicy) {
      const privacyPolicyQuestion = questionsWithOptionIds.find(
        ({ id }) => id === formValues.privacyPolicy!.id,
      ) as unknown as PrivacyPolicyQuestion;

      const privacyPolicyInput = {
        questionId: formValues.privacyPolicy.id,
        answers: privacyPolicyQuestion.options.filter(({ text }) => {
          const yesNo = formValues.privacyPolicy!.value ? 'Yes' : 'No';
          return text === yesNo;
        }),
      };
      questionnaireAnswers?.push(privacyPolicyInput);
    }

    const resumeInput = hasValidResumeId
      ? {
          id: resume.id,
          uri: resume.fileMetadata?.uri || '',
          idFromResumeResource: -1,
        }
      : null;

    type InputMaybe<T> = T | null | undefined;
    let coverLetterInput: InputMaybe<CoverLetterInput> = null;

    if (rootCoverLetterMethod === 'upload') {
      try {
        await getPromiseForKey(COVER_LETTER_KEY);
      } catch (coverLetterUploadError) {
        // Handle upload errors
        addLogByKey('ReviewPage-submitForm-promise-cover-letter-catch', {
          href: window.location.href,
          method: rootCoverLetterMethod,
          'coverLetter-uri-watch':
            watch('coverLetter-uri')?.slice(-5) || 'undefined',
          error: coverLetterUploadError,
        });
      }

      const coverLetterUri = watch('coverLetter-uri');
      (window as any)['reupload-coverLetter'] = !coverLetterUri;
      (window as any).coverLetterUri = coverLetterUri?.slice(-5) || 'undefined';

      const uploadCoverLetter: {
        uri: string;
      } = {
        uri: coverLetterUri
          ? coverLetterUri
          : await generateCoverletterUri(rootCoverLetterFile as File),
      };
      coverLetterInput = uploadCoverLetter;
    }

    if (rootCoverLetterMethod === 'change') {
      coverLetterInput = {
        writtenText: rootCoverLetterText,
        uri: await generateCoverletterUri(rootCoverLetterText as string),
      };
    }

    let selectionCeriteriaInput: InputMaybe<SelectionCriteriaInput> = null;

    if (rootSelectionCriteriaMethod === 'upload') {
      try {
        await getPromiseForKey(SELECTION_CRITERIA_KEY);
      } catch (selectionCriteriaUploadError) {
        // Handle upload errors
        addLogByKey('ReviewPage-submitForm-promise-selection-criteria-catch', {
          href: window.location.href,
          method: rootSelectionCriteriaMethod,
          'selectionCriteria-uri-watch':
            watch('selectionCriteria-uri')?.slice(-5) || 'undefined',
          error: selectionCriteriaUploadError,
        });
      }

      const selectionCriteriaUri = watch('selectionCriteria-uri');
      (window as any)['reupload-selectionCriteria'] = !selectionCriteriaUri;
      (window as any).selectionCriteriaUri =
        selectionCriteriaUri?.slice(-5) || 'undefined';

      const uploadSelectionCriteria: {
        uri: string;
      } = {
        uri: selectionCriteriaUri
          ? selectionCriteriaUri
          : await generateSelectionCriteriaUri(
              rootSelectionCriteriaFile as File,
            ),
      };
      selectionCeriteriaInput = uploadSelectionCriteria;
    }

    if (rootSelectionCriteriaMethod === 'change') {
      selectionCeriteriaInput = {
        writtenText: rootSelectionCriteriaText,
        uri: await generateSelectionCriteriaUri(
          rootSelectionCriteriaText as string,
        ),
      };
    }

    const referenceInput =
      referencesIdNamespace && referencesSelectionNamespace === 'include'
        ? { id: referencesIdNamespace }
        : null;

    try {
      if (
        (watch('coverLetter-uri') === undefined &&
          rootCoverLetterMethod === 'upload') ||
        (watch('selectionCriteria-uri') === undefined &&
          rootSelectionCriteriaMethod === 'upload')
      ) {
        logError(
          {
            type: 'submit-application-document-uri-invalid',
            message: 'submit application document uri invalid',
          },
          {
            debugData: getLog(),
            'reupload-coverLetter': (window as any)['reupload-coverLetter'],
            coverLetterUri: (window as any).coverLetterUri,
            'reupload-selectionCriteria': (window as any)[
              'reupload-selectionCriteria'
            ],
            selectionCriteriaUri: (window as any).selectionCriteriaUri,
          },
        );
      } else if (
        rootCoverLetterMethod === 'upload' ||
        rootSelectionCriteriaMethod === 'upload'
      ) {
        logInfo(
          'submit application document uri valid',
          'submit-application-document-uri-valid',
          {
            // debugData: getLog(),
            'reupload-coverLetter': (window as any)['reupload-coverLetter'],
            coverLetterUri: (window as any).coverLetterUri,
            'reupload-selectionCriteria': (window as any)[
              'reupload-selectionCriteria'
            ],
            selectionCriteriaUri: (window as any).selectionCriteriaUri,
          },
        );
      }

      const response = await submitApplication({
        variables: {
          input: {
            jobId,
            correlationId: applicationCorrelationId,
            zone,
            // TODO: formValues.profilePrivacyLevel causes tests to fail
            profilePrivacyLevel: getValues('profilePrivacyLevel'),
            ...(resumeInput !== null && { resume: resumeInput }),
            ...(coverLetterInput !== null && { coverLetter: coverLetterInput }),
            ...(selectionCeriteriaInput !== null && {
              selectionCriteria: selectionCeriteriaInput,
            }),
            mostRecentRole,
            questionnaireAnswers:
              questionnaireAnswers.length > 0
                ? questionnaireAnswers
                : undefined,
            ...(referenceInput !== null && {
              referenceCheck: referenceInput,
            }),
            priorityApply: {
              state: getPriorityApplyState(
                isShowStrongInterest,
                priorityApplyCredit?.credit2?.__typename,
              ),
            },
          },
          locale: config.VOCAB_LANGUAGE,
        },
      });

      if (
        response.data?.submitApplication.__typename ===
        'SubmitApplicationFailure'
      ) {
        // should be an error on: __typename: 'SubmitApplicationValidationError',
        const errorMessage =
          response.data?.submitApplication.errors.length > 0 &&
          response.data?.submitApplication.errors.map((x) => x.message)[0];

        logError(
          {
            type: 'submit-application-validation-graphql',
            message:
              errorMessage ||
              'Submit application failed due to validation errors.',
          },
          {
            response,
            applicationCorrelationId,
            jobId,
            operationName: getOperationNameFromQuery(SUBMIT_APPLICATION),
          },
        );
        throw new Error('SubmitApplicationFailure');
      }

      const applicationId =
        response.data?.submitApplication.__typename ===
        'SubmitApplicationSuccess'
          ? response.data?.submitApplication.applicationId || ''
          : '';

      trackSubmitSucceeded({
        profileVisibilityLevel: getValues('profilePrivacyLevel')?.toLowerCase(),
        previousProfileVisibility: profilePrivacyId
          ? profilePrivacyId.toLowerCase()
          : 'not set',
        currentPageSection: PageSection.PROFILE_VISIBILITY,
        currentPageSubsection: PageSubSection.VISIBILITY,
        actionOrigin: ActionOrigin.APPLY,
        applicationId,
        applicationCorrelationId,
        isResumeAttached: watch('resume-method') !== FileAttachment.NONE,
        isCoverLetterAttached:
          watch('coverLetter-method') !== FileAttachment.NONE,
        resumeHasSensitivePersonalInfo: Boolean(
          resume?.fileMetadata?.sensitiveDataInfo?.isDetected,
        ),
        isStrongInterest: isShowStrongInterest,
      });

      removeSessionStorage();

      const successUrl = `../success`;
      navigate(
        { pathname: successUrl, search: location.search },
        { replace: true },
      );

      logInfo(
        `Successful Application ${applicationCorrelationId}: jobId ${jobId}`,
        'submit-application-success',
        {
          correlationId: applicationCorrelationId,
          jobId,
        },
      );

      const durationMs = Date.now() - processStart.getTime();
      logInfo('Submit application duration', 'submit-application-duration', {
        durationMs,
      });
    } catch (e) {
      metrics.count('SUBMIT_APPLICATION_ERROR');
      logError(
        {
          type: 'submit-application-error',
          message: `for Application ${applicationCorrelationId}: jobId ${jobId}`,
        },
        {
          correlationId: applicationCorrelationId,
          jobId,
          error: e,
          operationName: getOperationNameFromQuery(SUBMIT_APPLICATION),
        },
      );
    }
  });

  const handleDocumentsChange = (values: Partial<DocumentsFormValues>) => {
    resetRoot({ ...getRootValues(), ...values }, { keepErrors: true });
  };

  const submitErrorMsg = getErrorMsg(t, submitState);

  const fieldNameTranslations = {
    resume: t('Resume'),
    coverLetter: t('Cover letter'),
    selectionCriteria: t('Selection criteria'),
    privacyPolicy: '', // no field name for privacy policy
    profilePrivacyLevel: t('Profile visibility level'), //  profile privacy level will always be set
  };

  const validationErrors = Object.entries(formState.errors);
  const validationMessages = validationErrors.reduce(
    (map, [key, fieldError]) => {
      const prefix = key.split('-')[0] as keyof typeof fieldNameTranslations;
      const fieldName = fieldNameTranslations[prefix];
      if (!fieldName || !fieldError) {
        return map;
      }
      return { ...map, [fieldName]: fieldError.message };
    },
    {},
  );

  const hasErrors = Boolean(submitErrorMsg) || validationErrors.length > 0;

  return (
    <>
      <Helmet>
        <title>{`${t('Review and submit')} | ${config.BRAND}`}</title>
      </Helmet>
      {loading ? (
        <PageLoader />
      ) : (
        <Stack space="xxlarge">
          {hasErrors && (
            // adding key to force re-mount to trigger scroll
            <ScrollOnMount key={formState.submitCount}>
              <AlertMessage message={submitErrorMsg || validationMessages} />
            </ScrollOnMount>
          )}

          <Box data={{ testid: 'personal-details-summary' }}>
            <PersonalDetails />
          </Box>

          <DocumentsSummary>
            <ResumeItem
              resume={resume as Resume}
              resumes={resumes as Resume[]}
              onChange={handleDocumentsChange}
            />
            <Divider />
            <CoverLetterItem onChange={handleDocumentsChange} />
            {shouldRenderselectionCriteria && (
              <>
                <Divider />
                <SelectionCriteriaItem onChange={handleDocumentsChange} />
              </>
            )}
            <Divider />
          </DocumentsSummary>

          {showQuestionsSummary && (
            <Stack space="large">
              <RoleRequirementsSummary />
              <PrivacyPolicyInput
                pageViewedFired={pageViewedFired}
                privacyPolicyMessage={
                  formState.errors.privacyPolicy?.message || ''
                }
                control={control as Control<any>}
                clearErrors={clearErrors}
                setValue={setValue}
              />
            </Stack>
          )}

          <Box data={{ testid: 'profile-summary' }}>
            <ProfileSummary
              isNewToWorkforce={isNewToWorkforce}
              onNewToWorkforceToggle={onNewToWorkforceToggle}
              profilePrivacyLevel={watch('profilePrivacyLevel')}
              skills={data?.viewer?.skills}
              roles={data?.viewer?.roles || []}
              unconfirmedRoles={unconfirmedData.roles}
              qualifications={data?.viewer?.qualifications as Qualification[]}
              unconfirmedQualifications={unconfirmedData.qualifications}
              licences={licences}
              advertiser={advertiser}
              referenceChecks={data?.viewer?.referenceChecks || []}
            />
          </Box>

          <ProfileVisibilitySection
            profileVisibilityOptions={profileVisibilityOptions}
            profilePrivacyId={profilePrivacyId}
          />

          {(isUnverified || isVerified) && (
            <DigitalIdentityToggle
              isShowStrongInterest={isShowStrongInterest}
              setIsShowStrongInterest={setIsShowStrongInterest}
              hasDigitalIdentityNudge={hasDigitalIdentityNudge}
              remainingCount={remainingCount}
              nextRenewalDate={nextRenewalDate}
              isVerified={isVerified}
            />
          )}

          <Stack space="large">
            <PrivacyPolicy />
            <Inline space="none">
              <Button
                data={{ testid: `review-submit-application` }}
                type="submit"
                tone="brandAccent"
                loading={formState.isSubmitting}
                icon={<IconSend />}
                onClick={() => {
                  const profileVisibilityLevel = getValues(
                    'profilePrivacyLevel',
                  )?.toLowerCase();
                  trackSubmitClick({ profileVisibilityLevel });
                  setLastApplicationLocalStorage(
                    referencesIdNamespace,
                    referencesSelectionNamespace,
                    LOCAL_STORAGE_KEY,
                  );
                  submitForm();
                }}
              >
                {t('Submit application')}
              </Button>
            </Inline>
          </Stack>
        </Stack>
      )}
    </>
  );
};

function getPriorityApplyState(
  isShowStrongInterest: boolean,
  credit2Typename: string | undefined,
) {
  if (!isShowStrongInterest) {
    return 'NotPrioritized';
  }
  return credit2Typename === 'PendingCredit' ? 'Pending' : 'Prioritized';
}

function isSubmitApplicationFailure(
  mutationResult: any,
): mutationResult is SubmitApplicationFailure {
  if (!mutationResult) {
    return false;
  }
  return mutationResult.__typename === 'SubmitApplicationFailure';
}

function getErrorMsg(
  t: (key: 'Sorry we couldnt') => string,
  { data, error }: MutationResult<ApplySubmitApplicationMutation>,
) {
  if (isSubmitApplicationFailure(data?.submitApplication)) {
    return data.submitApplication.errors?.map((x) => x.message)[0];
  }
  if (error) {
    return t('Sorry we couldnt');
  }
  return null;
}

export default ReviewPage;
